import { useEffect, useState } from 'react';
import Modal from '../common/Modal';
import { orderApi, vendorApi, userApi } from '../../api/services';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';

/**
 * Generic Hand Off modal. Pulls valid next-step reasons for the order's
 * current owner team and lets the user pick one.
 *
 * Props:
 *   isOpen, onClose, onDone
 *   orderId — required
 *   fromTeam — the order's current current_owner_team
 */
export default function HandoffModal({ isOpen, onClose, onDone, orderId, fromTeam }) {
  const [reasons, setReasons] = useState([]);
  const [reasonCode, setReasonCode] = useState('');
  const [reasonNote, setReasonNote] = useState('');
  const [vendors, setVendors] = useState([]);
  const [users, setUsers] = useState([]);
  const [toVendorId, setToVendorId] = useState('');
  const [toUserId, setToUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !fromTeam) return;
    setLoading(true);
    setReasonCode(''); setReasonNote(''); setToVendorId(''); setToUserId('');
    Promise.all([
      orderApi.availableHandoffs(fromTeam),
      vendorApi.list().catch(() => ({ data: { vendors: [] } })),
      userApi.list().catch(() => ({ data: { users: [] } })),
    ])
      .then(([rRes, vRes, uRes]) => {
        setReasons(rRes.data.reasons || []);
        setVendors(vRes.data.vendors || vRes.data || []);
        setUsers(uRes.data.users || uRes.data || []);
      })
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load handoff options'))
      .finally(() => setLoading(false));
  }, [isOpen, fromTeam]);

  const selectedReason = reasons.find((r) => r.reason_code === reasonCode);
  const needsVendor = selectedReason?.to_team === 'vendor';
  const canPickUser = selectedReason?.to_team === 'dispatch_user';

  async function handleSubmit() {
    if (!reasonCode) { toast.error('Pick a reason'); return; }
    setSubmitting(true);
    try {
      await orderApi.handoff(orderId, {
        toTeam: selectedReason.to_team,
        reasonCode,
        reasonNote: reasonNote || null,
        toVendorId: needsVendor ? (toVendorId || null) : null,
        toUserId: canPickUser ? (toUserId || null) : null,
      });
      toast.success(`Handed off: ${selectedReason.label}`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Handoff failed');
    } finally {
      setSubmitting(false);
    }
  }

  // Group reasons by category for a cleaner dropdown
  const grouped = reasons.reduce((acc, r) => {
    (acc[r.category] ||= []).push(r);
    return acc;
  }, {});

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Hand off order" size="md">
      {loading && <div className="text-sm text-gray-500">Loading options…</div>}
      {!loading && reasons.length === 0 && (
        <div className="text-sm text-gray-600">No handoff options available from this state.</div>
      )}
      {!loading && reasons.length > 0 && (
        <div className="space-y-3">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">Reason</span>
            <select
              value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              <option value="">— pick a reason —</option>
              {Object.entries(grouped).map(([cat, list]) => (
                <optgroup key={cat} label={cat.replace(/_/g, ' ')}>
                  {list.map((r) => (
                    <option key={r.reason_code} value={r.reason_code}>
                      {r.label} → {r.to_team}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>

          {selectedReason && (
            <div className="text-xs text-gray-500 -mt-1">{selectedReason.description}</div>
          )}

          {needsVendor && (
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Vendor</span>
              <select
                value={toVendorId} onChange={(e) => setToVendorId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">— pick a vendor —</option>
                {vendors.filter((v) => v.is_active !== false).map((v) => (
                  <option key={v.id} value={v.id}>{v.name} ({v.code})</option>
                ))}
              </select>
            </label>
          )}

          {canPickUser && (
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Assign to user (optional)</span>
              <select
                value={toUserId} onChange={(e) => setToUserId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">— team-level ownership —</option>
                {users.filter((u) => (u.roles || []).includes('dispatch_user')).map((u) => (
                  <option key={u.id} value={u.id}>{u.first_name} {u.last_name} · {u.email}</option>
                ))}
              </select>
            </label>
          )}

          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</span>
            <textarea
              value={reasonNote} onChange={(e) => setReasonNote(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button
              onClick={handleSubmit} disabled={!reasonCode || submitting}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Hand off
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
